import { MAX_RESULT_DEPTH, PAGE_SIZE_OPTIONS } from "@/lib/filters";
import { formatNumber } from "@/lib/format";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Select } from "./ui/select";

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  disabled?: boolean;
}

/**
 * Controles de paginação. Respeita o limite de profundidade da API
 * (`MAX_RESULT_DEPTH`): desabilita "próxima" além do que é navegável.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  disabled = false,
}: PaginationProps) {
  const maxDepthPage = Math.max(1, Math.floor(MAX_RESULT_DEPTH / pageSize));
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const effectiveMax = Math.min(totalPages, maxDepthPage);
  const canPrev = page > 1 && !disabled;
  const canNext = page < effectiveMax && !disabled;
  const depthLimited = totalPages > maxDepthPage;

  return (
    <div className="flex flex-col gap-3 border-t border-border px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <label htmlFor="page-size">Itens por página</label>
        <Select
          id="page-size"
          value={String(pageSize)}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          disabled={disabled}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex items-center gap-3">
        {depthLimited ? (
          <span className="hidden text-xs text-muted-foreground md:inline">
            Navegáveis os primeiros {formatNumber(MAX_RESULT_DEPTH)} resultados
          </span>
        ) : null}
        <span className="text-sm text-muted-foreground">
          Página {formatNumber(page)} de {formatNumber(effectiveMax)}
        </span>
        <div className="flex gap-1">
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(page - 1)}
            disabled={!canPrev}
            aria-label="Página anterior"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onPageChange(page + 1)}
            disabled={!canNext}
            aria-label="Próxima página"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
